/**
 * Capability flags for features whose contract has not merged yet.
 *
 * They are DERIVED from the generated surface, never hand-set. The day a contract merges,
 * the flag flips from the regenerated output and the drift gate forces a review of exactly
 * that diff. A hand-set flag would be a second source of truth that can be true while the
 * server disagrees.
 *
 * There is deliberately NO flag for usage. `getPortalUsage` is in the merged contract and
 * is implemented, so any contract-derived flag would read `true` — while the call still
 * 404s, because neither edge routes /v1/portal/* yet. The general limitation is worth
 * stating once: THE GENERATOR SEES CONTRACTS, NOT DEPLOYMENTS. Where a contract exists but
 * the path is unreachable, the degraded state must be driven by the RESPONSE, which is why
 * mapError carries a `usage` context that maps 404 to the degraded absence rather than to
 * NOT_FOUND copy.
 */
import { Me } from "./generated/identity.generated.js";
import { Subscription } from "./generated/billing.generated.js";
import { operations } from "./generated/index.js";

function hasField(schema: { shape: Record<string, unknown> }, field: string): boolean {
  return field in schema.shape;
}

export const capabilities = {
  /**
   * Me.preferences { pushToTalkKey, usageMetrics, notifications }.
   * Until it lands, three Settings rows render device-local with a note; the four genuinely
   * device-local rows are unaffected.
   */
  preferences: hasField(Me, "preferences"),

  /**
   * Subscription.paymentMethod / .billingAddress — read-only pass-through, not persisted.
   * Until it lands: "No card on file." / "Nothing will be charged automatically."
   * NEVER invented digits.
   */
  paymentMethod: hasField(Subscription, "paymentMethod"),

  /**
   * DELETE /v1/identity/me. Until it lands the danger zone renders with the button disabled
   * and a stated reason; "Nothing else is stored to delete." is unchanged, because it is
   * still true.
   */
  deleteAccount: "deleteMe" in operations,
} as const;

export type Capability = keyof typeof capabilities;
